// @ts-nocheck
/**
 * Single source of truth for 280G threshold / parachute PV math (matches former Analysis.tsx).
 * Used by Analysis and DocumentGeneration so shareholder documents align with results.
 */
import { getSharedStartDate } from '../../../utils/compensationStep';
import { getIsoYmdCalendarYear, parseLocalDateFromIsoYmd, parseWorkflowDate } from '../../../utils/isoDateDisplay';
import { sumBuyerEquityAwardsParachuteValue } from './buyerEquityAwardValue';
import { calculateEquityAwardQa24cValue } from './qa24cEquity';

export function build280gAnalysisResults(formData) {
  const individuals = formData.individuals || [];
  const uniqueNames = Array.from(new Set(individuals.map((i) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const dealPrice = +formData.dealPrice || 0;
  const closingYear = getIsoYmdCalendarYear(formData.closingDate) ?? new Date().getFullYear();
  const years = [closingYear - 5, closingYear - 4, closingYear - 3, closingYear - 2, closingYear - 1];
  const afr = (+formData.afr || 5) / 100;
  const discountRateForPV = 1.2 * afr;

  const calcPV = (fv, months) => {
    if (months <= 0 || fv <= 0) return fv;
    const yrs = months / 12;
    return fv / Math.pow(1 + discountRateForPV / 2, yrs * 2);
  };
  const calcStreamPV = (monthlyAmount, numMonths) => {
    if (monthlyAmount <= 0 || numMonths <= 0) return 0;
    let totalPV = 0;
    for (let m = 0; m < numMonths; m++) {
      totalPV += calcPV(monthlyAmount, m);
    }
    return totalPV;
  };

  return uniqueIndividuals.map((ind) => {
    const comp = formData.compensation?.[ind.id] || {};
    const baseSalary = +comp.baseSalary || 0;
    const bonusPct = +comp.bonusPct || 0;
    const targetBonus = baseSalary * bonusPct;
    const healthPremium = +comp.healthPremium || 1500;
    const baseD = formData.baseAmountData?.[ind.id] || {};
    const startDateStr = getSharedStartDate(formData, ind.id);
    const startDate = parseLocalDateFromIsoYmd(startDateStr);
    const startYear = startDate ? startDate.getFullYear() : 0;
    const specialPayments = +baseD.specialPayments || 0;

    const getAnnualizedComp = (year) => {
      const rawComp = +baseD[year] || 0;
      if (!rawComp || !startDate) return rawComp;
      if (startYear === year && year >= years[0] && year < closingYear) {
        const yearEnd = new Date(year, 11, 31);
        const daysWorked = Math.floor((yearEnd.getTime() - startDate.getTime()) / 864e5) + 1;
        if (daysWorked < 365 && daysWorked > 0) {
          return Math.round(Math.max(0, rawComp - specialPayments) * (365 / daysWorked) + specialPayments);
        }
      }
      return rawComp;
    };

    let base = 0;
    if (startYear === closingYear) {
      base = baseSalary;
    } else {
      const applicableYears = years.filter((y) => (!startDate || y >= startYear) && y < closingYear);
      const baseVals = applicableYears.map((y) => getAnnualizedComp(y)).filter((v) => v > 0);
      base = baseVals.length ? Math.round(baseVals.reduce((s, v) => s + v, 0) / baseVals.length) : 0;
    }
    const threshold = Math.max(0, base * 3 - 1);

    const sevD = formData.severanceData?.[ind.id] || {};
    const salaryAmount = +sevD.salaryAmount || 0;
    const salaryUnit = sevD.salaryUnit || 'months';
    const cobraMonths = +sevD.cobraMonths || 0;
    const otherSev = +sevD.other || 0;
    const paymentTiming = sevD.paymentTiming || 'lumpAtClose';
    const salaryMonths = salaryUnit === 'months' ? salaryAmount : salaryAmount / 4.33;
    let nominalSalaryAmt =
      salaryUnit === 'months' ? (baseSalary / 12) * salaryAmount : (baseSalary / 52) * salaryAmount;
    let nominalBonusAmt = 0;
    if (sevD.bonusPortion === 'prorated') {
      const dt = parseLocalDateFromIsoYmd(formData.closingDate) || parseWorkflowDate(formData.closingDate);
      if (dt) {
        const days = Math.floor((dt.getTime() - new Date(dt.getFullYear(), 0, 1).getTime()) / 864e5);
        nominalBonusAmt = (targetBonus * days) / 365;
      }
    } else if (sevD.bonusPortion === '50') nominalBonusAmt = targetBonus * 0.5;
    else if (sevD.bonusPortion === '100') nominalBonusAmt = targetBonus;
    const nominalCobraAmt = healthPremium * cobraMonths;
    const nominalSeverance = nominalSalaryAmt + nominalBonusAmt + nominalCobraAmt + otherSev;
    let pvSalaryAmt = nominalSalaryAmt,
      pvBonusAmt = nominalBonusAmt,
      pvCobraAmt = nominalCobraAmt,
      pvOtherSev = otherSev;
    if (paymentTiming === 'paidOverTime') {
      pvSalaryAmt = calcStreamPV(baseSalary / 12, salaryMonths);
      pvCobraAmt = calcStreamPV(healthPremium, cobraMonths);
    }
    const severancePV = pvSalaryAmt + pvBonusAmt + pvCobraAmt + pvOtherSev;

    const cic = +formData.cicBonusData?.[ind.id] || 0;

    const incD = formData.compIncreaseData?.[ind.id] || {};
    let increases = 0;
    if (incD.hasIncrease === 'yes') {
      increases = Math.max(
        0,
        baseSalary * (1 + bonusPct) - (+incD.priorBaseSalary || 0) * (1 + (+incD.priorBonusPct || 0))
      );
    }

    const eqD = formData.equityData?.[ind.id] || { awards: [] };
    const awards = eqD.awards || [];
    let accelEquity = 0;

    awards.forEach((award) => {
      const equityValue = calculateEquityAwardQa24cValue({
        award,
        closingDate: formData.closingDate,
        dealPrice,
        discountRateForPV,
      });
      accelEquity += equityValue.parachuteValue;
    });

    const buyerEqD = formData.buyerEquityData?.[ind.id] || { awards: [] };
    const buyerEquityAwards = sumBuyerEquityAwardsParachuteValue(buyerEqD.awards);

    const otherD = formData.otherPaymentsData?.[ind.id] || {};
    const otherPaymentsArray = otherD.payments || [];
    const otherPaymentsTotal = otherPaymentsArray.reduce((sum, p) => sum + (+p.amount || 0), 0);

    const parachutePV = severancePV + cic + accelEquity + increases + buyerEquityAwards + otherPaymentsTotal;
    const nominalParachute = nominalSeverance + cic + accelEquity + increases + buyerEquityAwards + otherPaymentsTotal;
    const difference = threshold - parachutePV;
    const hasIssue = difference < 0;

    return {
      ...ind,
      base,
      threshold,
      severance: severancePV,
      severanceNominal: nominalSeverance,
      cic,
      increases,
      accelEquity,
      buyerEquityAwards,
      otherPayments: otherPaymentsTotal,
      parachute: parachutePV,
      parachuteNominal: nominalParachute,
      difference,
      hasIssue,
      status: hasIssue ? 'over' : 'under',
      hasPVDiscount: Math.abs(nominalParachute - parachutePV) > 1,
    };
  });
}

/** DQI excess above the 280G threshold (zero when under threshold). */
export function excessForRow(r: { difference: number }) {
  return Math.max(0, -r.difference);
}

/** DQIs over the 280G threshold — Vote Room auto-exclude / §318 attribution (same `hasIssue` rule as DocumentGeneration). */
export function buildVoteRoomDqisFromFormData(formData) {
  return build280gAnalysisResults(formData || {})
    .filter((r) => r.hasIssue)
    .map((r) => ({
      name: r.name,
      title: String(r.title ?? '').trim() || '—',
    }));
}
